import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabaseClient';
import { 
  fetchProfile, 
  setTierInDB, 
  incrementVideoCountDB, 
  getRemainingVideosDB, 
  checkVideoLimitDB,
  incrementGreetingCountDB,
  getRemainingGreetingsDB,
  checkGreetingLimitDB,
  upsertProfile,
  TIER_LIMITS,
  normalizeTier,
} from '../utils/supabaseProfile';
import {
  peekRecordingDraftId,
  getRecordingDraftId,
  syncRecordingCache,
  restoreRecordingCache,
  clearActiveRecordingCache,
  purgeExpiredRecordingCache,
} from '../utils/recordingCache';
import { dedupeQuestionResponses, questionDedupeKey } from '../utils/questionResponses';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Load initial state from sessionStorage
  const loadFromStorage = (key, defaultValue) => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error(`Error loading ${key} from storage:`, error);
      return defaultValue;
    }
  };

  // Load from localStorage (for persistent data across sessions)
  const loadFromLocalStorage = (key, defaultValue) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error(`Error loading ${key} from localStorage:`, error);
      return defaultValue;
    }
  };

  // User Information — persisted in localStorage so it survives dev reloads and new tabs
  const [userInfo, setUserInfo] = useState(() => loadFromLocalStorage('userInfo', {
    fullName: '',
    email: '',
    location: '',
    ageRange: '',
    gender: '',
    phone: '',
  }));

  // Generate unique user ID if not exists (for tracking without authentication)
  const [userId] = useState(() => loadFromLocalStorage('userId', `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`));

  // Subscription Tier (freemium model)
  const [subscriptionTier, setSubscriptionTier] = useState(() => loadFromLocalStorage('subscriptionTier', 'free'));

  // Admin Status (for admin dashboard access) - synced from database
  const [isAdmin, setIsAdmin] = useState(false);

  // Video Usage Tracking per tier (cached from database for offline resilience)
  // free: 1 video/month, max 5 min per clip
  // oneoff: 1 video per purchase, max 5 min per clip
  // subscription: unlimited videos, max 5 min per clip
  // oneoff: 1 video total (per purchase)
  // subscription: unlimited
  const [videoUsage, setVideoUsage] = useState(() => loadFromLocalStorage('videoUsage', {
    count: 0,
    lastResetDate: new Date().toISOString(),
  }));

  // Greeting Card Usage Tracking (cached from database for offline resilience)
  const [greetingUsage, setGreetingUsage] = useState(() => loadFromLocalStorage('greetingUsage', {
    count: 0,
    lastResetDate: new Date().toISOString(),
    monthlyLimit: 3
  }));

  // Video Package Selection — all advertised plans use 10 questions / 5 minutes per answer
  const [selectedPackage, setSelectedPackage] = useState(() => loadFromStorage('selectedPackage', {
    option: 1,
    questions: 10,
    price: 0,
    recordingTimeLimit: 300,
  }));

  // Template Selection
  const [selectedTemplate, setSelectedTemplate] = useState(() => loadFromStorage('selectedTemplate', null));

  // Payment Status
  const [paymentCompleted, setPaymentCompleted] = useState(() => loadFromStorage('paymentCompleted', false));
  
  // Access Token for video creation form
  const [accessToken, setAccessToken] = useState(() => loadFromStorage('accessToken', null));

  // Video Creation Form Data
  const [videoCreationData, setVideoCreationData] = useState(() => loadFromStorage('videoCreationData', {
    videoTitle: '',
    questions: [], // Array of { question: '', responseType: 'text'|'audio'|'video', response: '', skipped: boolean }
    credits: [], // Array of names
    coverPhoto: null,
    verified: false,
    allowPublicShowcase: false,
  }));

  // True after IndexedDB hydrate attempt finishes (success or empty)
  const [recordingsReady, setRecordingsReady] = useState(false);
  const recordingsReadyRef = useRef(false);
  const recordingSyncTimer = useRef(null);
  const lastSyncedMediaKey = useRef('');

  // Terms acceptance
  const [termsAccepted, setTermsAccepted] = useState(() => loadFromStorage('termsAccepted', false));

  // Persist userInfo to localStorage so it survives page reloads and dev restarts
  useEffect(() => {
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
  }, [userInfo]);

  useEffect(() => {
    sessionStorage.setItem('selectedPackage', JSON.stringify(selectedPackage));
  }, [selectedPackage]);

  useEffect(() => {
    sessionStorage.setItem('selectedTemplate', JSON.stringify(selectedTemplate));
  }, [selectedTemplate]);

  useEffect(() => {
    sessionStorage.setItem('paymentCompleted', JSON.stringify(paymentCompleted));
  }, [paymentCompleted]);

  useEffect(() => {
    sessionStorage.setItem('accessToken', JSON.stringify(accessToken));
  }, [accessToken]);

  useEffect(() => {
    // Metadata in sessionStorage; Blobs live in IndexedDB (see recordingCache)
    const dataToStore = {
      ...videoCreationData,
      coverPhoto: videoCreationData.coverPhoto instanceof Blob ? undefined : videoCreationData.coverPhoto,
      questions: videoCreationData.questions?.map(q => ({
        ...q,
        videoBlob: undefined,
        audioBlob: undefined,
        response: q.responseType === 'text' ? q.response : undefined
      }))
    };
    sessionStorage.setItem('videoCreationData', JSON.stringify(dataToStore));
  }, [videoCreationData]);

  // Restore recording Blobs from IndexedDB after refresh / hot reload
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      try {
        await purgeExpiredRecordingCache();
        const draftId = peekRecordingDraftId();
        const rawQuestions = videoCreationData.questions || [];

        if (draftId) {
          const restored = await restoreRecordingCache(
            draftId,
            rawQuestions,
            videoCreationData.coverPhoto
          );
          if (!cancelled) {
            const changed =
              restored.restoredCount > 0 ||
              restored.questions.length !== rawQuestions.length ||
              restored.questions.some((q, i) => q !== rawQuestions[i]);
            if (changed) {
              setVideoCreationData((prev) => ({
                ...prev,
                questions: restored.questions,
                coverPhoto: restored.coverPhoto ?? prev.coverPhoto,
              }));
            }
            if (restored.restoredCount > 0) {
              console.log(`♻️ Restored ${restored.restoredCount} recording(s) from local cache`);
            }
            if (restored.questions.length !== rawQuestions.length) {
              console.log(`🧹 Deduped question responses: ${rawQuestions.length} → ${restored.questions.length}`);
            }
          }
        } else if (rawQuestions.length > 1) {
          const deduped = dedupeQuestionResponses(rawQuestions);
          if (!cancelled && deduped.length !== rawQuestions.length) {
            setVideoCreationData((prev) => ({ ...prev, questions: deduped }));
            console.log(`🧹 Deduped question responses: ${rawQuestions.length} → ${deduped.length}`);
          }
        }
      } catch (err) {
        console.warn('Recording cache hydrate failed:', err);
      } finally {
        if (!cancelled) {
          recordingsReadyRef.current = true;
          setRecordingsReady(true);
        }
      }
    };

    hydrate();
    return () => { cancelled = true; };
    // Intentionally once on mount — sessionStorage seed is already in state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep IndexedDB in sync while the draft has media.
  // Cleared explicitly on successful submit, reset, or TTL purge — not on empty
  // transient state (avoids wiping cache during hydrate).
  useEffect(() => {
    if (!recordingsReady) return;

    const questions = videoCreationData.questions || [];
    const coverPhoto = videoCreationData.coverPhoto;
    const hasMedia = questions.some(
      (q) =>
        (q.videoBlob instanceof Blob && q.videoBlob.size > 0) ||
        (q.audioBlob instanceof Blob && q.audioBlob.size > 0)
    ) || (coverPhoto instanceof Blob && coverPhoto.size > 0);

    if (!hasMedia) return;

    // Fingerprint media only — ignore title/credits edits so we don't rewrite GB of video
    const mediaKey = questions
      .map((q) => {
        const b = q.videoBlob || q.audioBlob;
        if (!(b instanceof Blob)) return q.skipped ? 's' : '';
        return `${q.responseType}:${b.size}:${b.type}`;
      })
      .join('|') + (coverPhoto instanceof Blob ? `|c:${coverPhoto.size}` : '');

    if (mediaKey === lastSyncedMediaKey.current) return;

    if (recordingSyncTimer.current) clearTimeout(recordingSyncTimer.current);

    recordingSyncTimer.current = setTimeout(async () => {
      try {
        const draftId = getRecordingDraftId();
        await syncRecordingCache(draftId, questions, coverPhoto);
        lastSyncedMediaKey.current = mediaKey;
        console.log('💾 Recordings cached locally (survive refresh)');
      } catch (err) {
        console.warn('Recording cache sync failed:', err);
      }
    }, 600);

    return () => {
      if (recordingSyncTimer.current) clearTimeout(recordingSyncTimer.current);
    };
    // mediaKey captures blob size/type changes without depending on whole object identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    recordingsReady,
    videoCreationData.questions,
    videoCreationData.coverPhoto,
  ]);

  useEffect(() => {
    sessionStorage.setItem('termsAccepted', JSON.stringify(termsAccepted));
  }, [termsAccepted]);

  // Save to localStorage for persistent data
  useEffect(() => {
    localStorage.setItem('userId', JSON.stringify(userId));
  }, [userId]);

  useEffect(() => {
    localStorage.setItem('subscriptionTier', JSON.stringify(subscriptionTier));
  }, [subscriptionTier]);

  // Silently sync subscription tier and usage from Supabase in the background after login.
  // Does NOT block the app — localStorage values are used immediately on load.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Sync on sign-in AND on initial session restore so userInfo/tier are always fresh
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        fetchProfile().then(async (profile) => {
          // Create missing profile with email (NOT NULL) so tier updates don't fail
          if (!profile) {
            try {
              const meta = session.user.user_metadata || {};
              profile = await upsertProfile({
                email: session.user.email,
                full_name: meta.full_name || '',
                subscription_tier: 'free',
                ...(meta.phone ? { phone: meta.phone } : {}),
                ...(meta.age_range ? { age_range: meta.age_range } : {}),
                ...(meta.marketing_list_consent != null
                  ? { marketing_list_consent: Boolean(meta.marketing_list_consent) }
                  : {}),
              });
            } catch (createErr) {
              console.warn('Could not auto-create profile:', createErr.message);
              setUserInfo((prev) => ({
                ...prev,
                fullName: session.user.user_metadata?.full_name || prev.fullName || '',
                email: session.user.email || prev.email || '',
              }));
              return;
            }
          }

          // Hydrate userInfo from Supabase profile + session so it survives reloads
          setUserInfo(prev => ({
            ...prev,
            fullName: profile.full_name || session.user.user_metadata?.full_name || prev.fullName || '',
            email: session.user.email || profile.email || prev.email || '',
            phone: profile.phone || prev.phone || '',
            ageRange: profile.age_range || prev.ageRange || '',
          }));

          // Sync subscription tier
          if (profile.subscription_tier) {
            setSubscriptionTier(profile.subscription_tier);
          }
          
          // Sync admin status
          if (profile.is_admin !== undefined) {
            setIsAdmin(profile.is_admin);
          }
          
          const now = new Date();
          
          // Sync video usage
          const videoLastReset = new Date(profile.videos_reset_date || now);
          const videoNeedsReset =
            now.getMonth() !== videoLastReset.getMonth() ||
            now.getFullYear() !== videoLastReset.getFullYear();
          setVideoUsage({
            count: videoNeedsReset ? 0 : (profile.videos_this_month || 0),
            lastResetDate: videoNeedsReset ? now.toISOString() : (profile.videos_reset_date || now.toISOString()),
          });
          
          // Sync greeting card usage
          const greetingLastReset = new Date(profile.greeting_cards_reset_date || now);
          const greetingNeedsReset =
            now.getMonth() !== greetingLastReset.getMonth() ||
            now.getFullYear() !== greetingLastReset.getFullYear();
          setGreetingUsage({
            count: greetingNeedsReset ? 0 : (profile.greeting_cards_this_month || 0),
            lastResetDate: greetingNeedsReset ? now.toISOString() : (profile.greeting_cards_reset_date || now.toISOString()),
            monthlyLimit: 3
          });
        }).catch(() => {}); // never block the app on a failed sync
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Cache video and greeting usage to localStorage for offline resilience
  useEffect(() => {
    localStorage.setItem('videoUsage', JSON.stringify(videoUsage));
  }, [videoUsage]);

  useEffect(() => {
    localStorage.setItem('greetingUsage', JSON.stringify(greetingUsage));
  }, [greetingUsage]);

  // Helper function to set package
  const selectPackage = (option) => {
    // All active plans use the same 10-question / 5-minute-per-clip package
    const packages = {
      1: { option: 1, questions: 10, price: 0, recordingTimeLimit: 300 },
      2: { option: 2, questions: 10, price: 350, recordingTimeLimit: 300 },
      3: { option: 3, questions: 10, price: 14.99, recordingTimeLimit: 300 },
    };
    setSelectedPackage(packages[option] || packages[1]);
  };

  // Update video creation data
  const updateVideoCreationData = (updates) => {
    setVideoCreationData(prev => ({
      ...prev,
      ...updates
    }));
  };

  // Upsert question response by question text (never append duplicates)
  const addQuestionResponse = (questionData) => {
    setVideoCreationData((prev) => {
      const key = questionDedupeKey(questionData?.question);
      const existing = prev.questions || [];
      const idx = key
        ? existing.findIndex((q) => questionDedupeKey(q.question) === key)
        : -1;

      if (idx >= 0) {
        const next = existing.slice();
        next[idx] = { ...next[idx], ...questionData };
        return { ...prev, questions: next };
      }

      return {
        ...prev,
        questions: [...existing, questionData],
      };
    });
  };

  // Update question response
  const updateQuestionResponse = (index, updates) => {
    setVideoCreationData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, ...updates } : q
      )
    }));
  };

  // Add credit
  const addCredit = (name) => {
    setVideoCreationData(prev => ({
      ...prev,
      credits: [...prev.credits, name]
    }));
  };

  // Remove credit
  const removeCredit = (index) => {
    setVideoCreationData(prev => ({
      ...prev,
      credits: prev.credits.filter((_, i) => i !== index)
    }));
  };

  // Reset all data (for starting over)
  const resetAllData = () => {
    setUserInfo({ fullName: '', email: '', location: '', ageRange: '', gender: '', phone: '' });
    setSelectedPackage({ option: 1, questions: 10, price: 0, recordingTimeLimit: 300 });
    setSelectedTemplate(null);
    setPaymentCompleted(false);
    setAccessToken(null);
    setVideoCreationData({ videoTitle: '', questions: [], credits: [], coverPhoto: null, verified: false });
    setTermsAccepted(false);
    sessionStorage.clear();
    lastSyncedMediaKey.current = '';
    clearActiveRecordingCache().catch(() => {});
  };

  // Generate access token after payment
  const generateAccessToken = () => {
    const token = `SL-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setAccessToken(token);
    return token;
  };

  // Tier limits — imported from supabaseProfile (single source of truth)
  const getTierLimits = () => TIER_LIMITS[normalizeTier(subscriptionTier)] || TIER_LIMITS.free;

  // Video usage helpers - now uses database
  const checkVideoLimit = async () => {
    const tier = normalizeTier(subscriptionTier);
    // First check local state for immediate feedback
    if (tier === 'subscription') return true;
    
    // Check database for accurate count
    try {
      const result = await checkVideoLimitDB();
      // Only hard-block on definitive quota reasons. Profile fetch failures
      // used to return allowed:false (not_authenticated) and hid Start Recording
      // / Test Camera — looking like a broken camera.
      if (!result.allowed && (result.reason === 'limit_reached' || result.reason === 'oneoff_used')) {
        console.warn('Video limit reached:', result.message || result.reason);
        return false;
      }
      if (!result.allowed && result.reason === 'not_authenticated') {
        console.warn('Video limit check: profile unavailable, allowing record (re-checked at submit)');
        return true;
      }
      return result.allowed !== false;
    } catch (error) {
      console.error('Error checking video limit:', error);
      // Fallback to local state if database check fails
      if (tier === 'oneoff') return videoUsage.count < 1;
      const now = new Date();
      const lastReset = new Date(videoUsage.lastResetDate);
      if (now.getMonth() !== lastReset.getMonth() || now.getFullYear() !== lastReset.getFullYear()) {
        setVideoUsage({ count: 0, lastResetDate: now.toISOString() });
        return true;
      }
      return videoUsage.count < 1;
    }
  };

  const incrementVideoCount = () => {
    // Update local state immediately for UI feedback
    setVideoUsage(prev => ({ ...prev, count: prev.count + 1 }));
    // Sync to database in background
    incrementVideoCountDB().catch(e => console.warn('incrementVideoCountDB failed:', e.message));
  };

  const getRemainingVideos = async () => {
    if (normalizeTier(subscriptionTier) === 'subscription') return 'unlimited';
    
    // Try to get from database first
    try {
      return await getRemainingVideosDB();
    } catch (error) {
      console.error('Error getting remaining videos:', error);
      // Fallback to local state — free = 1/month, oneoff = 1 purchase
      if (normalizeTier(subscriptionTier) === 'oneoff') return Math.max(0, 1 - videoUsage.count);
      const now = new Date();
      const lastReset = new Date(videoUsage.lastResetDate);
      if (now.getMonth() !== lastReset.getMonth() || now.getFullYear() !== lastReset.getFullYear()) return 1;
      return Math.max(0, 1 - videoUsage.count);
    }
  };

  // Subscription Management — also persists to Supabase
  const setTier = (tier) => {
    setSubscriptionTier(tier);
    setTierInDB(tier).catch(e => console.warn('setTierInDB failed:', e.message));
  };

  const upgradeToPremium = () => {
    setTier('subscription');
    return true;
  };

  const downgradeTofree = () => {
    setTier('free');
    return true;
  };

  const isPremium = () => subscriptionTier === 'subscription';

  // Admin Management
  const toggleAdminStatus = () => {
    setIsAdmin(prev => !prev);
  };

  // Greeting Usage Management - now uses database
  const checkGreetingLimit = async () => {
    const tier = normalizeTier(subscriptionTier);
    // Paid plans are not capped for greetings
    if (tier === 'subscription' || tier === 'oneoff') return true;
    
    // Check database for accurate count
    try {
      const result = await checkGreetingLimitDB();
      if (!result.allowed) {
        console.warn('Greeting limit reached:', result.message);
      }
      return result.allowed;
    } catch (error) {
      console.error('Error checking greeting limit:', error);
      // Fallback to local state if database check fails
      const now = new Date();
      const lastReset = new Date(greetingUsage.lastResetDate);
      if (now.getMonth() !== lastReset.getMonth() || now.getFullYear() !== lastReset.getFullYear()) {
        setGreetingUsage({ count: 0, lastResetDate: now.toISOString(), monthlyLimit: 3 });
        return true;
      }
      return greetingUsage.count < greetingUsage.monthlyLimit;
    }
  };

  const incrementGreetingCount = () => {
    // Update local state immediately for UI feedback
    setGreetingUsage(prev => ({
      ...prev,
      count: prev.count + 1
    }));
    // Sync to database in background
    incrementGreetingCountDB().catch(e => console.warn('incrementGreetingCountDB failed:', e.message));
  };

  const getRemainingGreetings = async () => {
    if (normalizeTier(subscriptionTier) === 'subscription' || normalizeTier(subscriptionTier) === 'oneoff') {
      return 'unlimited';
    }
    
    // Try to get from database first
    try {
      return await getRemainingGreetingsDB();
    } catch (error) {
      console.error('Error getting remaining greetings:', error);
      // Fallback to local state
      const now = new Date();
      const lastReset = new Date(greetingUsage.lastResetDate);
      if (now.getMonth() !== lastReset.getMonth() || now.getFullYear() !== lastReset.getFullYear()) return 3;
      return Math.max(0, greetingUsage.monthlyLimit - greetingUsage.count);
    }
  };


  const value = {
    // State
    userInfo,
    userId,
    selectedPackage,
    selectedTemplate,
    paymentCompleted,
    accessToken,
    videoCreationData,
    termsAccepted,
    subscriptionTier,
    greetingUsage,
    videoUsage,
    isAdmin,
    recordingsReady,
    
    // Setters
    setUserInfo,
    selectPackage,
    setSelectedTemplate,
    setPaymentCompleted,
    setAccessToken,
    setVideoCreationData,
    setTermsAccepted,
    setSubscriptionTier: setTier,   // writes to Supabase + local state
    setGreetingUsage,
    
    // Helper functions
    updateVideoCreationData,
    addQuestionResponse,
    updateQuestionResponse,
    addCredit,
    removeCredit,
    resetAllData,
    generateAccessToken,
    clearRecordingCache: clearActiveRecordingCache,
    
    // Subscription Management
    upgradeToPremium,
    downgradeToFree: downgradeTofree,
    isPremium,
    getTierLimits,
    checkVideoLimit,
    incrementVideoCount,
    getRemainingVideos,
    
    // Admin Management
    toggleAdminStatus,
    
    // Greeting Usage
    checkGreetingLimit,
    incrementGreetingCount,
    getRemainingGreetings
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

